import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO, { serviceSchema } from '../components/SEO';
import { servicesPages } from '../mock';

export default function ServiceDetail() {
  const { slug } = useParams();
  const data = servicesPages[slug];

  if (!data) return <Navigate to="/" replace />;

  return (
    <>
      <SEO
        title={`${data.title} · momentIA`}
        description={data.subtitle}
        path={`/servicios/${slug}`}
        schema={serviceSchema({ name: data.title, description: data.subtitle, path: `/servicios/${slug}` })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-14 lg:pt-20 pb-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 -left-32 w-[500px] h-[500px] bg-violet-600/15 rounded-full blur-[120px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-violet-300 mb-5 hover:text-violet-200 transition-colors">
                <span>Servicios</span>
                <span>/</span>
                <span className="text-white/60">{data.title}</span>
              </Link>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                {data.hero}
              </h1>
              <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">{data.subtitle}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/request-demo">
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold">
                    Solicitar demo
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/precios">
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    Ver precios
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-violet-900/30">
                <img src={data.image} alt={data.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/80 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-black/60 backdrop-blur-xl border border-white/10 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-400/30 flex items-center justify-center text-violet-300">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">Impacto medible</div>
                    <div className="text-xs text-white/60">Analytics en tiempo real</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-4">Beneficios</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              Lo que conseguís con momentIA
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {data.benefits.map((b) => (
              <div key={b.title} className="card-lift p-7 rounded-2xl bg-[#111116] border border-white/8">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-violet-500/10 border border-violet-400/25 flex items-center justify-center text-violet-300 shrink-0">
                    <Check className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold mb-2">{b.title}</h3>
                    <p className="text-white/60 text-[15px] leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-24 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-4">Casos de uso</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              Perfecto para
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {data.useCases.map((u) => (
              <div
                key={u}
                className="px-5 py-3 rounded-full border border-white/10 bg-[#111116] text-white/80 text-sm hover:border-violet-400/40 hover:bg-violet-500/5 hover:text-white transition-colors"
              >
                {u}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-violet-400/20 bg-gradient-to-br from-[#141425] via-[#101018] to-[#0e0e14] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="relative">
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
                Llevá tu {data.title.toLowerCase()} a otro nivel.
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                Hablá con nuestro equipo y armá una prueba a medida para tu próximo evento.
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to="/request-demo">
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold">
                    Solicitar demo
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/precios">
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    Ver precios
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
